import * as React from "react";
import type { NumberField, NumberFieldRootChangeEventDetails } from "@base-ui/react/number-field";

type UseNumberFieldLogicParams = {
  step: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  onValueChange?: NumberField.Root.Props["onValueChange"];
};

export interface BaseNumberFieldProps extends Omit<React.ComponentProps<typeof NumberField.Root>, "render"> {
  id?: string;
  label?: React.ReactNode;
  error?: boolean;
  size?: "small" | "medium";
  step?: number;
  helperText?: React.ReactNode;
  roundToStep?: boolean;
  onBlur?: () => void;
  onValueChange?: (value: number | null, eventDetails?: NumberFieldRootChangeEventDetails) => void;
  showErrorIcon?: boolean;
}

type OnValueChange = NonNullable<NumberField.Root.Props["onValueChange"]>;

export function useNumberFieldLogic({ step, min, max, disabled, onValueChange }: UseNumberFieldLogicParams) {
  const clamp = React.useCallback(
    (val: number) => Math.max(min ?? Number.NEGATIVE_INFINITY, Math.min(max ?? Number.POSITIVE_INFINITY, val)),
    [min, max],
  );

  const handleValueChange = React.useCallback<OnValueChange>(
    (value, eventDetails) => {
      switch (eventDetails.reason) {
        case "input-change": {
          onValueChange?.(value, eventDetails);
          break;
        }

        case "increment-press": {
          if (disabled || (max !== undefined && value !== null && value >= max)) {
            return;
          }

          const val = value ?? 0;

          if (step == 1) {
            onValueChange?.(clamp(val), eventDetails);
            break;
          }

          const remainder = val % step;
          const next = val + (step - remainder || step);

          onValueChange?.(clamp(next), eventDetails);
          break;
        }

        case "decrement-press": {
          if (disabled || (min !== undefined && value !== null && value < min)) {
            return;
          }

          const val = value ?? 0;

          if (step == 1) {
            onValueChange?.(clamp(val), eventDetails);
            break;
          }

          const remainder = val % step;
          const next = val - (remainder || step);

          onValueChange?.(clamp(next), eventDetails);
          break;
        }
      }
    },
    [step, min, max, disabled, clamp, onValueChange],
  );

  return { handleValueChange };
}
